import styled, { css } from 'styled-components'
import { Check } from 'lucide-react'
import { SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'
import { QuestionLabel, InputWrapper, interactiveBase, selectedStyle } from './shared'

interface CheckboxQuestionProps {
  question: string
  value: string[]
  options: string[]
  onChange: (value: string[]) => void
}

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
  width: 100%;
`

const OptionCard = styled.button<{ $selected: boolean }>`
  ${interactiveBase}
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
  padding: ${SPACING.md} ${SPACING.lg};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.regular};
  text-align: left;
  border-width: 1px;
  border-style: solid;
  border-radius: ${BORDER_RADIUS.lg};
  min-height: 48px;

  ${({ $selected }) =>
    $selected &&
    css`
      ${selectedStyle}
      font-weight: ${FONT_WEIGHT.medium};
    `}
`

const CheckIndicator = styled.span<{ $selected: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 2px solid
    ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-border-dark)')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
  background: ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'transparent')};
`

const OptionText = styled.span`
  flex: 1;
  line-height: 1.4;
`

export function CheckboxQuestion({
  question,
  value,
  options,
  onChange,
}: CheckboxQuestionProps): React.JSX.Element {
  const toggle = (opt: string): void => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt])
  }

  return (
    <InputWrapper>
      <QuestionLabel>{question}</QuestionLabel>
      <OptionList role="group" aria-label={question}>
        {options.map((opt) => {
          const selected = value.includes(opt)
          return (
            <OptionCard
              key={opt}
              type="button"
              $selected={selected}
              onClick={() => toggle(opt)}
              aria-pressed={selected}
            >
              <CheckIndicator $selected={selected}>
                {selected && <Check size={12} color="white" strokeWidth={3} />}
              </CheckIndicator>
              <OptionText>{opt}</OptionText>
            </OptionCard>
          )
        })}
      </OptionList>
    </InputWrapper>
  )
}
